"use client";

import type { ReactNode } from "react";
import { useEffect } from "react";
import { useRouter } from "next/router";
import { BootLoading } from "@src/context/BootLoadingProvider/BootLoadingProvider";
import { useActiveOrganization } from "@src/context/OrganizationProvider/OrganizationProvider";
import { useWallet } from "@src/context/WalletProvider";
import { useIsOnboarded } from "@src/hooks/useIsOnboarded";
import { useReturnTo } from "@src/hooks/useReturnTo/useReturnTo";
import { useUser } from "@src/hooks/useUser";
import { useLeaseExistenceQuery } from "@src/queries/useLeaseQuery";
const ONBOARDING_ROUTE = "/onboarding";
type GateDecision = "loading" | "render" | "toOnboarding" | "toReturn";

/** A not-yet-onboarded user may stay on these routes (they deploy their first app, fund it, create an API key, or join and run an organization here). */
const ONBOARDING_ALLOWED_PREFIXES = ["/new-deployment/configure", "/billing", "/invitations", "/members", "/projects", "/user/api-keys"];
export const DEPENDENCIES = {
  useUser,
  useWallet,
  useActiveOrganization,
  useLeaseExistenceQuery,
  useIsOnboarded,
  useReturnTo,
  useRouter
};
type Props = {
  children: ReactNode;
  /** True on `definePublicPage` routes (login/signup/marketing). Public pages are never onboarding-gated. */
  isPublic?: boolean;
  dependencies?: typeof DEPENDENCIES;
};
export function RequireOnboarding({
  children,
  isPublic,
  dependencies: d = DEPENDENCIES
}: Props) {
  return <LeaseBasedGate isPublic={isPublic} dependencies={d} data-sentry-element="LeaseBasedGate" data-sentry-component="RequireOnboarding" data-sentry-source-file="RequireOnboarding.tsx">
      {children}
    </LeaseBasedGate>;
}

/** `true` for `/deployments/{dseq}` detail routes (numeric dseq) — not the `/deployments` list. */
function isDeploymentDetail(path: string) {
  return /^\/deployments\/\d+/.test(path);
}
function LeaseBasedGate({
  children,
  isPublic,
  dependencies: d
}: Required<Pick<Props, "children">> & {
  isPublic?: boolean;
  dependencies: typeof DEPENDENCIES;
}) {
  const router = d.useRouter();
  const {
    user,
    isLoading: isUserLoading
  } = d.useUser();
  const {
    address,
    hasWallet
  } = d.useWallet();
  const {
    isLoading: isOrganizationLoading,
    isError: organizationsErrored
  } = d.useActiveOrganization();
  const {
    returnTo
  } = d.useReturnTo({
    defaultReturnTo: "/"
  });
  const hasWalletAddress = hasWallet && !!address;
  const leaseExistenceQuery = d.useLeaseExistenceQuery(address, {
    enabled: hasWalletAddress
  });
  const isLeasesLoading = hasWalletAddress && leaseExistenceQuery.isLoading;
  const leasesErrored = hasWalletAddress && leaseExistenceQuery.isError;
  const isOnboarded = d.useIsOnboarded();
  const path = router.asPath.split("?")[0];
  const isOnOnboarding = path === ONBOARDING_ROUTE;
  const isAllowed = ONBOARDING_ALLOWED_PREFIXES.some(prefix => path.startsWith(prefix)) || isDeploymentDetail(path);
  const identityKnown = !isUserLoading && !isOrganizationLoading;
  const leasesSettled = !isLeasesLoading;
  const decision = decideLeaseGate({
    isPublic: !!isPublic,
    identityKnown,
    leasesSettled,
    leasesErrored,
    organizationsErrored,
    isAuthenticated: !!user?.userId,
    isOnboarded,
    isOnOnboarding,
    isAllowed
  });
  useEffect(function routeByOnboardingState() {
    if (decision === "toOnboarding") router.replace(ONBOARDING_ROUTE);else if (decision === "toReturn") router.replace(returnTo);
  }, [decision, returnTo, router]);
  if (decision === "render") return <>{children}</>;
  return <BootLoading data-sentry-element="BootLoading" data-sentry-component="LeaseBasedGate" data-sentry-source-file="RequireOnboarding.tsx" />;
}

/**
 * Resolves the lease-based gate's action, in priority order. Public pages always render (RequireAuth owns auth,
 * and the WalletProvider boot gate guarantees the wallet lookup has settled before this gate mounts).
 * Allow-list pages (configure, a deployment detail) render *immediately*, ahead of the identity wait and
 * the leases query, because they own their own loading UX (e.g. the auto-deploy progress overlay). Billing is
 * allow-listed for a different reason: it is where a trial converts, so bouncing a user who came to pay would
 * turn every link into it into a dead end. The `/onboarding` picker likewise renders once identity is known
 * without waiting for leases: its trial wallet provisions in the background, and the leases query flips to
 * loading the moment that wallet's address appears, which would otherwise flash the full-screen loader and
 * remount the page (including an open Add Credits sheet).
 * An already-onboarded user who lands on `/onboarding` is still sent back where they came from, but only once
 * leases resolve (they render the picker until then). Everywhere else first waits until user identity is
 * known, then for leases to settle: an onboarded user renders, a not-onboarded user is sent to `/onboarding`. A
 * leases *error* leaves onboarding unknowable (an undefined result is not "no leases"), so we fail open and render
 * where the user is rather than eject a genuinely onboarded user into the first-deploy funnel on a transient
 * chain-API blip.
 *
 * `isOnboarded` (see `useIsOnboarded`) is already true for a skipper or a team organization as soon as identity is
 * known, so they never wait on leases and are never sent back into the funnel. Memberships that fail to load leave
 * the team case unknowable, so the gate fails open as it does for leases.
 */
function decideLeaseGate(input: {
  isPublic: boolean;
  identityKnown: boolean;
  leasesSettled: boolean;
  leasesErrored: boolean;
  organizationsErrored: boolean;
  isAuthenticated: boolean;
  isOnboarded: boolean;
  isOnOnboarding: boolean;
  isAllowed: boolean;
}): GateDecision {
  if (input.isPublic) return "render";
  if (input.isAllowed) return "render";
  if (!input.identityKnown) return "loading";
  if (!input.isAuthenticated) return "render";
  if (input.isOnboarded) return input.isOnOnboarding ? "toReturn" : "render";
  if (input.isOnOnboarding) return "render";
  if (!input.leasesSettled) return "loading";
  if (input.leasesErrored || input.organizationsErrored) return "render";
  return "toOnboarding";
}